var _excluded = ["children", "tooltip", "selectedKeys", "defaultSelectedKeys", "disabledKeys", "onChange", "onSelectionChange", "onOpenChange", "onScroll", "label", "description", "errorMessage", "isRequired", "isDisabled", "isReadOnly", "validationState", "isQuiet", "labelPosition", "labelAlign", "necessityIndicator", "contextualHelp", "inputValue", "defaultInputValue", "onInputChange", "shouldFocusWrap", "loadingState", "menuTrigger", "align", "direction", "shouldFlip", "menuWidth", "allowsCustomValue", "formValue", "validationBehavior", "autoFocus", "name", "id", "isHidden", "onFocus", "onBlur", "onFocusChange", "onKeyDown", "onKeyUp", "onSearchTextChange", "selectedItemLabels", "UNSAFE_className", "aria-label", "aria-labelledby", "aria-describedby", "aria-details"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useId, useMemo, useRef } from 'react';
import { Field } from '@react-spectrum/label';
import { FocusRing } from '@react-aria/focus';
import { Popover } from '@react-spectrum/overlays';
import { useUnwrapDOMRef } from '@react-spectrum/utils';
import { useOverlayTriggerState } from '@react-stately/overlays';
import ChevronDownMedium from '@spectrum-icons/ui/ChevronDownMedium';
import AlertMedium from '@spectrum-icons/ui/AlertMedium';
import { ProgressCircle } from '@adobe/react-spectrum';
import cl from 'classnames';
import { EMPTY_FUNCTION, ensureArray } from '@deephaven/utils';
import { useMergeRef } from '@deephaven/react-hooks';
import { normalizeTooltipOptions, wrapItemChildren } from "../utils/index.js";
import { flattenJsxChildren, flattenEntriesToItems } from "./multiSelectUtils.js";
import { useMultiSelectState } from "./useMultiSelectState.js";
import { useMultiSelectFilter } from "./useMultiSelectFilter.js";
import { useMultiSelectKeyboard } from "./useMultiSelectKeyboard.js";
import { useMultiSelectLoadingSpinner } from "./useMultiSelectLoadingSpinner.js";
import { useMultiSelectScrollListener } from "./useMultiSelectScrollListener.js";
import { MultiSelectTag } from "./MultiSelectTag.js";
import { MultiSelectListBox } from "./MultiSelectListBox.js";
import "./MultiSelect.css";
/**
 * Convert a Spectrum dimension value (e.g. `"size-3000"`) to its CSS variable
 * form. Numbers are converted to px. Already-valid CSS strings pass through.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function toCssDimension(value) {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'number') {
    return "".concat(value, "px");
  }
  if (/^(size|static-size)-/.test(value)) {
    return "var(--spectrum-global-dimension-".concat(value, ")");
  }
  return value;
}

/**
 * Multi-select styled to match Spectrum ComboBox. Renders selected items as
 * tags inside the trigger area alongside a filter input. Accepts the same
 * `Item` / `Section` JSX children as `Picker`.
 */
function MultiSelectInner(props, forwardedRef) {
  var _ref2, _toCssDimension, _triggerRef$current2;
  var children = props.children,
    _props$tooltip = props.tooltip,
    tooltip = _props$tooltip === void 0 ? true : _props$tooltip,
    propSelectedKeys = props.selectedKeys,
    defaultSelectedKeys = props.defaultSelectedKeys,
    propDisabledKeys = props.disabledKeys,
    propOnChange = props.onChange,
    propOnSelectionChange = props.onSelectionChange,
    onOpenChange = props.onOpenChange,
    _props$onScroll = props.onScroll,
    onScroll = _props$onScroll === void 0 ? EMPTY_FUNCTION : _props$onScroll,
    label = props.label,
    description = props.description,
    errorMessage = props.errorMessage,
    _props$isRequired = props.isRequired,
    isRequired = _props$isRequired === void 0 ? false : _props$isRequired,
    _props$isDisabled = props.isDisabled,
    isDisabled = _props$isDisabled === void 0 ? false : _props$isDisabled,
    _props$isReadOnly = props.isReadOnly,
    isReadOnly = _props$isReadOnly === void 0 ? false : _props$isReadOnly,
    validationState = props.validationState,
    _props$isQuiet = props.isQuiet,
    isQuiet = _props$isQuiet === void 0 ? false : _props$isQuiet,
    _props$labelPosition = props.labelPosition,
    labelPosition = _props$labelPosition === void 0 ? 'top' : _props$labelPosition,
    labelAlign = props.labelAlign,
    necessityIndicator = props.necessityIndicator,
    contextualHelp = props.contextualHelp,
    controlledInputValue = props.inputValue,
    _props$defaultInputVa = props.defaultInputValue,
    defaultInputValue = _props$defaultInputVa === void 0 ? '' : _props$defaultInputVa,
    onInputChange = props.onInputChange,
    _props$shouldFocusWra = props.shouldFocusWrap,
    shouldFocusWrap = _props$shouldFocusWra === void 0 ? false : _props$shouldFocusWra,
    loadingState = props.loadingState,
    _props$menuTrigger = props.menuTrigger,
    menuTrigger = _props$menuTrigger === void 0 ? 'input' : _props$menuTrigger,
    _props$align = props.align,
    align = _props$align === void 0 ? 'start' : _props$align,
    _props$direction = props.direction,
    direction = _props$direction === void 0 ? 'bottom' : _props$direction,
    _props$shouldFlip = props.shouldFlip,
    shouldFlip = _props$shouldFlip === void 0 ? true : _props$shouldFlip,
    menuWidth = props.menuWidth,
    _props$allowsCustomVa = props.allowsCustomValue,
    allowsCustomValue = _props$allowsCustomVa === void 0 ? false : _props$allowsCustomVa,
    _props$formValue = props.formValue,
    formValue = _props$formValue === void 0 ? 'key' : _props$formValue,
    _props$validationBeha = props.validationBehavior,
    validationBehavior = _props$validationBeha === void 0 ? 'aria' : _props$validationBeha,
    _props$autoFocus = props.autoFocus,
    autoFocus = _props$autoFocus === void 0 ? false : _props$autoFocus,
    name = props.name,
    id = props.id,
    _props$isHidden = props.isHidden,
    isHidden = _props$isHidden === void 0 ? false : _props$isHidden,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    onFocusChange = props.onFocusChange,
    onKeyDown = props.onKeyDown,
    onKeyUp = props.onKeyUp,
    onSearchTextChange = props.onSearchTextChange,
    selectedItemLabels = props.selectedItemLabels,
    UNSAFE_className = props.UNSAFE_className,
    ariaLabel = props['aria-label'],
    ariaLabelledby = props['aria-labelledby'],
    ariaDescribedby = props['aria-describedby'],
    ariaDetails = props['aria-details'],
    styleProps = _objectWithoutProperties(props, _excluded);

  // Spectrum's onOpenChange omits the trigger reason; relay it via ref.
  var lastTriggerReasonRef = useRef(undefined);
  var handleOverlayOpenChange = useCallback(isOpen => {
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(isOpen, lastTriggerReasonRef.current);
  }, [onOpenChange]);
  var overlayState = useOverlayTriggerState({
    onOpenChange: handleOverlayOpenChange
  });
  var openOverlay = useCallback(reason => {
    lastTriggerReasonRef.current = reason;
    overlayState.open();
  }, [overlayState]);
  var closeOverlay = useCallback(() => {
    lastTriggerReasonRef.current = undefined;
    overlayState.close();
  }, [overlayState]);
  var listBoxId = useId();
  var placement = "".concat(direction, " ").concat(align);
  var triggerRef = useRef(null);
  var inputRef = useRef(null);
  var popoverRef = useRef(null);
  var unwrappedPopoverRef = useUnwrapDOMRef(popoverRef);
  var listBoxRef = useRef(null);
  var unwrappedListBoxRef = useUnwrapDOMRef(listBoxRef);
  var isFocusedRef = useRef(false);
  var mergedTriggerRef = useMergeRef(triggerRef, forwardedRef);

  // Ensures Item/ItemContent wrapping for tooltips/overflow.
  var tooltipOptions = useMemo(() => normalizeTooltipOptions(tooltip), [tooltip]);
  var wrappedChildren = useMemo(() => ensureArray(wrapItemChildren(children, tooltipOptions)), [children, tooltipOptions]);

  // Flat {key,label} entries for filter/keyboard hooks. ListBox renders JSX directly.
  var allEntries = useMemo(() => flattenJsxChildren(wrappedChildren), [wrappedChildren]);
  var allItems = useMemo(() => flattenEntriesToItems(allEntries), [allEntries]);
  var allKeys = useMemo(() => allItems.map(i => i.key), [allItems]);

  // Persistent label cache: accumulates labels from items as they appear in
  // children. When server-side search filters items out of the children, the
  // cache ensures tags still display the correct label.
  var labelCacheRef = useRef(new Map());
  var itemLabelMap = useMemo(() => {
    var cache = labelCacheRef.current;
    allItems.forEach(i => cache.set(i.key, i.label));
    return new Map(cache);
  }, [allItems]);
  var getLabelFor = useCallback(key => {
    var _ref, _itemLabelMap$get;
    return (_ref = (_itemLabelMap$get = itemLabelMap.get(key)) !== null && _itemLabelMap$get !== void 0 ? _itemLabelMap$get : selectedItemLabels === null || selectedItemLabels === void 0 ? void 0 : selectedItemLabels.get(key)) !== null && _ref !== void 0 ? _ref : key;
  }, [itemLabelMap, selectedItemLabels]);
  var _useMultiSelectFilter = useMultiSelectFilter({
      allEntries,
      wrappedChildren,
      inputValue: controlledInputValue,
      defaultInputValue,
      onInputChange,
      onSearchTextChange
    }),
    searchText = _useMultiSelectFilter.searchText,
    setSearchText = _useMultiSelectFilter.setSearchText,
    filteredItems = _useMultiSelectFilter.filteredItems,
    filteredJsxChildren = _useMultiSelectFilter.filteredJsxChildren;
  var emptyMessage = useMemo(() => {
    if (filteredItems.length > 0) {
      return undefined;
    }
    if (loadingState === 'loading') {
      return 'Loading...';
    }
    // loadingMore + empty: defer to ListBox's loader pill instead of "No results".
    if (loadingState === 'loadingMore') {
      return undefined;
    }
    return 'No results';
  }, [filteredItems.length, loadingState]);
  var _useMultiSelectState = useMultiSelectState({
      selectedKeys: propSelectedKeys,
      defaultSelectedKeys,
      disabledKeys: propDisabledKeys,
      onChange: propOnChange,
      onSelectionChange: propOnSelectionChange,
      allKeys
    }),
    selectedKeys = _useMultiSelectState.selectedKeys,
    selectedKeyArray = _useMultiSelectState.selectedKeyArray,
    listBoxDisabledKeys = _useMultiSelectState.listBoxDisabledKeys,
    toggleKey = _useMultiSelectState.toggleKey,
    applyListBoxSelection = _useMultiSelectState.applyListBoxSelection;
  var _useMultiSelectKeyboa = useMultiSelectKeyboard({
      filteredItems,
      allItems,
      shouldFocusWrap,
      overlayState,
      openOverlay,
      closeOverlay,
      isReadOnly,
      isDisabled,
      searchText,
      setSearchText,
      selectedKeys,
      toggleKey,
      allowsCustomValue,
      menuTrigger,
      onKeyDown,
      listBoxContainerRef: unwrappedListBoxRef,
      inputRef
    }),
    handleInputKeyDown = _useMultiSelectKeyboa.handleInputKeyDown;
  useMultiSelectScrollListener({
    containerRef: unwrappedListBoxRef,
    isOpen: overlayState.isOpen,
    onScroll
  });
  var shouldShowInlineSpinner = useMultiSelectLoadingSpinner({
    loadingState,
    searchText,
    isOpen: overlayState.isOpen,
    menuTrigger
  });
  var refocusInput = useCallback(() => {
    requestAnimationFrame(() => {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    });
  }, []);
  var handleTagRemove = useCallback(key => {
    toggleKey(key);
    refocusInput();
  }, [toggleKey, refocusInput]);
  var handleListBoxSelectionChange = useCallback(selection => {
    if (isReadOnly) {
      return;
    }
    applyListBoxSelection(selection, filteredItems);
    refocusInput();
  }, [isReadOnly, applyListBoxSelection, filteredItems, refocusInput]);
  var handleInputFocus = useCallback(e => {
    if (isFocusedRef.current) {
      return;
    }
    isFocusedRef.current = true;
    if (menuTrigger === 'focus' && !overlayState.isOpen && !isDisabled) {
      openOverlay('focus');
    }
    onFocus === null || onFocus === void 0 || onFocus(e);
    onFocusChange === null || onFocusChange === void 0 || onFocusChange(true);
  }, [onFocus, onFocusChange, menuTrigger, overlayState, isDisabled, openOverlay]);
  var handleInputBlur = useCallback(e => {
    var related = e.relatedTarget;
    if (related != null) {
      if (triggerRef.current != null && triggerRef.current.contains(related)) {
        return;
      }
      if (unwrappedPopoverRef.current != null && unwrappedPopoverRef.current.contains(related)) {
        return;
      }
    } else {
      // relatedTarget can be null when clicking non-focusable content.
      // Use rAF to check if focus moved back into the component.
      requestAnimationFrame(() => {
        var active = document.activeElement;
        if (active != null && (triggerRef.current != null && triggerRef.current.contains(active) || unwrappedPopoverRef.current != null && unwrappedPopoverRef.current.contains(active))) {
          return;
        }
        isFocusedRef.current = false;
        if (overlayState.isOpen) {
          closeOverlay();
        }
        onBlur === null || onBlur === void 0 || onBlur(e);
        onFocusChange === null || onFocusChange === void 0 || onFocusChange(false);
      });
      return;
    }
    isFocusedRef.current = false;
    if (overlayState.isOpen) {
      closeOverlay();
    }
    onBlur === null || onBlur === void 0 || onBlur(e);
    onFocusChange === null || onFocusChange === void 0 || onFocusChange(false);
  }, [onBlur, onFocusChange, overlayState, closeOverlay, unwrappedPopoverRef]);
  var handleTriggerAreaClick = useCallback(() => {
    var _inputRef$current2;
    if (isDisabled) {
      return;
    }
    if (!overlayState.isOpen) {
      openOverlay('manual');
    }
    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
  }, [isDisabled, overlayState, openOverlay]);
  var handleChevronClick = useCallback(e => {
    var _inputRef$current3;
    // Stop trigger-area handler from re-opening the popover on close.
    e.stopPropagation();
    if (isDisabled) {
      return;
    }
    if (overlayState.isOpen) {
      closeOverlay();
    } else {
      openOverlay('manual');
    }
    (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 || _inputRef$current3.focus();
  }, [isDisabled, overlayState, openOverlay, closeOverlay]);
  return /*#__PURE__*/_jsx(Field
  // eslint-disable-next-line react/jsx-props-no-spreading
  , _objectSpread(_objectSpread({}, styleProps), {}, {
    label: label,
    description: description,
    errorMessage: validationState === 'invalid' ? errorMessage : undefined,
    isRequired: isRequired,
    isDisabled: isDisabled,
    validationState: validationState,
    labelPosition: labelPosition,
    labelAlign: labelAlign,
    necessityIndicator: necessityIndicator,
    contextualHelp: contextualHelp,
    wrapperClassName: cl('dh-multi-select', UNSAFE_className),
    children: /*#__PURE__*/_jsxs("div", {
      style: isHidden ? {
        display: 'none'
      } : undefined,
      children: [/*#__PURE__*/_jsx(FocusRing, {
        within: true,
        focusRingClass: "focus-ring",
        focusClass: "is-focused",
        children: /*#__PURE__*/_jsxs("div", {
          ref: mergedTriggerRef,
          id: id,
          onClick: handleTriggerAreaClick,
          className: cl('dh-multi-select-trigger', {
            'is-disabled': isDisabled,
            'is-read-only': isReadOnly,
            'is-quiet': isQuiet,
            'is-invalid': validationState === 'invalid'
          }),
          children: [/*#__PURE__*/_jsxs("div", {
            className: "dh-multi-select-content",
            children: [selectedKeyArray.map(key => /*#__PURE__*/_jsx(MultiSelectTag, {
              tagKey: key,
              label: getLabelFor(key),
              isDisabled: isDisabled,
              isReadOnly: isReadOnly,
              onRemove: handleTagRemove
            }, key)), /*#__PURE__*/_jsx("input", {
              ref: inputRef,
              className: "dh-multi-select-input",
              value: searchText,
              onChange: e => setSearchText(e.target.value),
              onFocus: handleInputFocus,
              onBlur: handleInputBlur,
              onKeyDown: handleInputKeyDown,
              onKeyUp: onKeyUp,
              disabled: isDisabled,
              readOnly: isReadOnly
              // eslint-disable-next-line jsx-a11y/no-autofocus
              ,
              autoFocus: autoFocus,
              role: "combobox",
              "aria-haspopup": "listbox",
              "aria-expanded": overlayState.isOpen,
              "aria-controls": overlayState.isOpen ? listBoxId : undefined,
              "aria-autocomplete": "list",
              "aria-label": ariaLabel,
              "aria-labelledby": ariaLabelledby,
              "aria-describedby": ariaDescribedby,
              "aria-details": ariaDetails
            })]
          }), shouldShowInlineSpinner && /*#__PURE__*/_jsx("div", {
            className: "dh-multi-select-loading-circle",
            children: /*#__PURE__*/_jsx(ProgressCircle, {
              "aria-label": "Loading",
              isIndeterminate: true,
              size: "S"
            })
          }), validationState === 'invalid' && !isDisabled && /*#__PURE__*/_jsx("div", {
            className: "dh-multi-select-invalid-icon",
            "aria-hidden": "true",
            children: /*#__PURE__*/_jsx(AlertMedium, {})
          }), /*#__PURE__*/_jsx("div", {
            className: cl('dh-multi-select-chevron', {
              'is-open': overlayState.isOpen
            }),
            onClick: handleChevronClick
            // Suppress default to keep input focus on chevron click.
            ,
            onPointerDown: e => e.preventDefault(),
            role: "button",
            tabIndex: -1,
            "aria-label": "Toggle dropdown",
            children: /*#__PURE__*/_jsx(ChevronDownMedium, {})
          })]
        })
      }), name != null && /*#__PURE__*/_jsx("input", {
        type: "hidden",
        name: name,
        value: formValue === 'text' ? selectedKeyArray.map(getLabelFor).join(',') : selectedKeyArray.join(','),
        required: validationBehavior === 'native' && isRequired ? true : undefined
      }), overlayState.isOpen && !isDisabled && /*#__PURE__*/_jsx(Popover, {
        ref: popoverRef,
        triggerRef: triggerRef,
        state: overlayState,
        hideArrow: true,
        isNonModal: true,
        placement: placement,
        shouldFlip: shouldFlip,
        shouldCloseOnInteractOutside: target => {
          var _triggerRef$current;
          return ((_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.contains(target)) !== true;
        },
        UNSAFE_style: {
          width: (_ref2 = (_toCssDimension = toCssDimension(menuWidth)) !== null && _toCssDimension !== void 0 ? _toCssDimension : (_triggerRef$current2 = triggerRef.current) === null || _triggerRef$current2 === void 0 ? void 0 : _triggerRef$current2.offsetWidth) !== null && _ref2 !== void 0 ? _ref2 : undefined
        },
        children: /*#__PURE__*/_jsx(MultiSelectListBox, {
          listBoxRef: listBoxRef,
          listBoxId: listBoxId,
          loadingState: loadingState,
          selectedKeys: selectedKeys,
          disabledKeys: listBoxDisabledKeys,
          onSelectionChange: handleListBoxSelectionChange,
          ariaLabel: typeof label === 'string' ? label : 'Options',
          emptyMessage: emptyMessage,
          children: filteredJsxChildren
        })
      })]
    })
  }));
}

/** Forwarded-ref wrapper. Trigger is a <div>, matching Picker's DOMRef shape. */
export var MultiSelect = /*#__PURE__*/React.forwardRef(MultiSelectInner);
MultiSelect.displayName = 'MultiSelect';
export default MultiSelect;
//# sourceMappingURL=MultiSelect.js.map